import { Status } from '@/components/AdminPanel/Shared/constants';
import { convertActiveStatus } from '@/components/AdminPanel/Shared/helpers/convertActiveStatus';
import { FAQ_ROUTES } from '@/components/Shared/routes/routes';
import { type DictTypes } from '@/constants';
import { isValidUrl, keysToCamelCase, keysToSnakeCase } from '@/services';
import { type TMenuItem } from '@/types/app';
import { type GetDictContentType } from '@/types/dicts';

import type {
  TFaq,
  TFaqForm,
  TFaqNested,
  TFaqRequest,
  TFaqResponse,
  TFaqSection
} from '../types/faq.types';
import {
  assignChildrenMaxOrderNoLength,
  assignChildrenOrderNo,
  countDotsAndNotDots,
  sortArticlesRecursively
} from './faqHelpers';

export const faqMapperFromResponse = (response: TFaqResponse): TFaq => {
  const camelCased = keysToCamelCase(response);

  const isValidExternalUrl: boolean = isValidUrl(camelCased.url);

  return {
    ...camelCased,
    faqPagesPositions: camelCased.faqPagesPositions.map(
      (position) => position.position.uuid
    ),
    externalLink: isValidExternalUrl ? (camelCased.url ?? '') : '',
    isActive: convertActiveStatus.toStr(camelCased.isActive),
    name: camelCased.name ?? '',
    template: camelCased.template ?? ''
  };
};

export const faqMapperToForm = (data: TFaq): TFaqForm => {
  return {
    acceptedPositionsUuid: data.faqPagesPositions,
    externalLink: data.externalLink,
    faqPagesTypeUuid: data.faqPagesTypeUuid,
    isActive: data.isActive,
    isByUrl: !!data.externalLink,
    moduleUuid: data.moduleUuid,
    name: data.name,
    parentName: '',
    parentUuid: data.parentUuid,
    position: data.position !== null ? data.position.toString() : null,
    template: data.template,
    uuid: data.uuid
  };
};

const getSortedStringifiedArray = (array: string[]): string => {
  return JSON.stringify([...array].sort((a, b) => a.localeCompare(b)));
};

export const faqMapperToRequest = (
  data: TFaqForm,
  targetFaqItem?: TFaq | null
): TFaqRequest => {
  const {
    acceptedPositionsUuid,
    isByUrl
  }: { acceptedPositionsUuid: string[]; isByUrl: boolean } = data;
  const snakeCased = keysToSnakeCase(data);

  const isAcceptedPositionsChanged: boolean =
    !targetFaqItem ||
    getSortedStringifiedArray(targetFaqItem.faqPagesPositions) !==
      getSortedStringifiedArray(acceptedPositionsUuid);

  return {
    accepted_positions_uuid: isAcceptedPositionsChanged
      ? acceptedPositionsUuid
      : undefined,
    faq_pages_type_uuid: snakeCased.faq_pages_type_uuid,
    is_active: convertActiveStatus.toBoolean(snakeCased.is_active),
    module_uuid: snakeCased.module_uuid || null,
    name: snakeCased.name,
    parent_uuid: snakeCased.parent_uuid || null,
    position: Number(snakeCased.position || 0),
    template: isByUrl ? '' : snakeCased.template,
    url: isByUrl ? snakeCased.external_link || '' : '',
    uuid: snakeCased.uuid
  };
};

/**
 * Преобразует плоский список FAQ в древовидную структуру.
 * Строит иерархию на основе parentUuid, проверяя принадлежность к одной группе.
 * Инициализирует статусы элементов (disabled, extension и т.д.).
 */
const faqMapperToNestedList = (faqList: TFaq[]): TFaqNested[] => {
  const map = new Map<string, TFaqNested>();

  const formattedFaqList = faqList.map((faqItem) => ({
    ...faqItem,
    template: faqItem.template.trim()
  }));

  formattedFaqList.forEach((faq) => {
    map.set(faq.uuid, {
      ...faq,
      children: [],
      extension: faq.externalLink?.split('.').pop() || null,
      maxDotsAndNotDotsOnLevel: [0, 0],
      orderNo: '',
      status: {
        hasMatchingChildren: false,
        isDisabled: false,
        isDisabledByParent: false,
        isDisabledByPosition: false,
        isSelfDisabled:
          faq.isActive === Status.disabled ||
          (faq.externalLink
            ? !isValidUrl(faq.externalLink)
            : !faq.template.trim().length)
      }
    });
  });

  const roots: TFaqNested[] = [];

  formattedFaqList.forEach((faq) => {
    const node = map.get(faq.uuid)!;
    if (faq.parentUuid) {
      const parent = map.get(faq.parentUuid);
      // Если родитель не найден или из другой группы — статья становится корневой
      if (parent && parent.faqPagesTypeUuid === faq.faqPagesTypeUuid) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  return sortArticlesRecursively(roots);
};

/**
 * Преобразует плоский список FAQ в структуру секций для отображения.
 * Выполняет несколько этапов обработки:
 * 1. Построение дерева из плоского списка
 * 2. Группировка по типам страниц (faqPagesTypes)
 * 3. Определение статусов disabled для всех элементов
 * 4. Фильтрация неактивных элементов (для пользовательской страницы)
 * 5. Сортировка, нумерация и расчет ширины для отображения
 */
export const faqMapperToSections = (
  faqList: TFaq[],
  faqPagesTypes: GetDictContentType<DictTypes.FaqPagesTypes>[],
  isAdminPage: boolean
): TFaqSection[] => {
  if (!faqList || faqList.length === 0) {
    return [];
  }

  const faqNestedList = faqMapperToNestedList(faqList);

  const determineIsDisabled = (
    faqItem: TFaqNested,
    isParentDisabled = false
  ): TFaqNested => {
    const processedChildren = faqItem.children.map((child) =>
      determineIsDisabled(
        child,
        faqItem.isActive === Status.disabled || isParentDisabled
      )
    );

    const hasActiveChildren = processedChildren.some(
      (child) => !child.status.isDisabled
    );

    let isDisabled = false;
    let isDisabledByParent = false;

    if (isParentDisabled) {
      isDisabled = true;
      isDisabledByParent = true;
    } else if (faqItem.isActive === Status.disabled) {
      isDisabled = true;
    } else {
      const hasContent: boolean =
        !!faqItem.externalLink?.trim().length ||
        !!faqItem.template.trim().length;

      if (!hasContent && !hasActiveChildren) {
        isDisabled = true;
      }
    }

    return {
      ...faqItem,
      status: {
        ...faqItem.status,
        isDisabled,
        isDisabledByParent
      },
      children: processedChildren
    };
  };

  const filterActiveElements = (faqItem: TFaqNested): TFaqNested | null => {
    const filteredChildren = faqItem.children
      .map(filterActiveElements)
      .filter((child): child is TFaqNested => child !== null);

    if (isAdminPage) {
      return {
        ...faqItem,
        children: filteredChildren
      };
    }

    // Для пользовательской страницы: показываем элемент, если он активен или есть активные дочерние элементы
    if (faqItem.status.isDisabled && filteredChildren.length === 0) {
      return null;
    }

    return {
      ...faqItem,
      children: filteredChildren
    };
  };

  // Секция для статей, не привязанных ни к одной группе (только для админ-панели)
  const notAssignedFaqsSection: TFaqSection = {
    articles: faqNestedList.filter(
      (faqItem) =>
        !faqItem.faqPagesTypeUuid ||
        !faqPagesTypes.some(
          (faqPageType) => faqPageType.uuid === faqItem.faqPagesTypeUuid
        )
    ),
    maxDotsAndNotDotsOnLevel: [0, 0],
    name: 'Не привязанные статьи',
    uuid: 'not-assigned'
  };

  return faqPagesTypes
    .slice()
    .sort(
      (a, b) =>
        Number(a.position || 0) - Number(b.position || 0) ||
        a.name.localeCompare(b.name)
    )
    .map((faqPageType) => {
      return {
        maxDotsAndNotDotsOnLevel: [0, 0],
        name: faqPageType.name,
        uuid: faqPageType.uuid,
        articles: faqNestedList.filter(
          (faqItem) => faqItem.faqPagesTypeUuid === faqPageType.uuid
        )
      } as TFaqSection;
    })
    .concat(isAdminPage ? notAssignedFaqsSection : [])
    .filter((faqSection) => faqSection.articles.length > 0)
    .map((faqSection) => {
      const articlesWithDisabled = faqSection.articles.map((article) =>
        determineIsDisabled(article, false)
      );

      const filteredArticles = articlesWithDisabled
        .map(filterActiveElements)
        .filter((article): article is TFaqNested => article !== null);

      return {
        ...faqSection,
        articles: filteredArticles
      };
    })
    .filter((faqSection) => faqSection.articles.length > 0)
    .map((faqSection, faqSectionIndex) => {
      let order = 1;

      const numiratedArticles = faqSection.articles.map((article) => {
        const orderNo = !article.status.isDisabled
          ? `${faqSectionIndex + 1}.${order++}`
          : '';
        return assignChildrenOrderNo(article, orderNo);
      });

      const maxDotsAndNotDotsOnLevel = numiratedArticles
        .filter((article) => !article.status.isDisabled)
        .map((article) => countDotsAndNotDots(article.orderNo))
        .reduce(
          (max, current) => [
            Math.max(max[0], current[0]),
            Math.max(max[1], current[1])
          ],
          [0, 0]
        );

      return {
        ...faqSection,
        articles: numiratedArticles.map((article) =>
          assignChildrenMaxOrderNoLength(article, maxDotsAndNotDotsOnLevel)
        ),
        maxDotsAndNotDotsOnLevel
      };
    })
    .filter((faqSection) => faqSection.articles.length > 0);
};

/**
 * Преобразует секции FAQ в структуру меню для навигации.
 * Рекурсивно обрабатывает вложенные элементы.
 * Ссылки и externalLink добавляются только для активных элементов.
 */
export const faqMapperToMenuItemList = (
  faqSections: TFaqSection[]
): TMenuItem[] => {
  const mapFaqToMenuItems = (faqItems: TFaqNested[]): TMenuItem[] => {
    return faqItems.map((faq) => {
      const isActive: boolean =
        !faq.status.isDisabledByPosition && !faq.status.isSelfDisabled;

      const result: TMenuItem = {
        id: faq.uuid,
        label: faq.name
      };

      if (faq.children.length > 0) {
        result.children = mapFaqToMenuItems(faq.children);
      }

      if (faq.extension) {
        result.extension = faq.extension;
      }

      if (isActive && faq.externalLink) {
        result.externalLink = faq.externalLink;
      }

      if (isActive && !!faq.template) {
        result.link = `${FAQ_ROUTES.root}${FAQ_ROUTES.faqArticle(faq.uuid)}`;
      }

      return result;
    });
  };

  return faqSections.map((module) => ({
    children: mapFaqToMenuItems(module.articles),
    id: module.uuid,
    label: module.name
  }));
};
